import { useEffect, useState } from "react";
import { Skeleton, Tooltip, Button, message } from "antd";
import { EventAnalytics, GetEventWhatsappBilling, ExportEventAnalyticsCsv } from "../../../../../apis/events";
import {
  activeUserIcon,
  DollarSign,
  faceIcon,
  ImageUpload,
  photoDistributedIcon,
  Rekognition,
  S3Logo,
  SystemUpload,
  totalPhotoIcon,
} from "../../../../../assets/images";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";

export const EventAnalyticsCard = ({ id, user }) => {
  const [loading, setLoading] = useState(false);
  const [eventAnalytics, setEventAnalytics] = useState(null);
  const [waCost, setWaCost] = useState(null);
  const [exporting, setExporting] = useState(false);

  const getEventAnalytics = async () => {
    setLoading(true);
    try {
      const [res, billingRes] = await Promise.all([
        EventAnalytics({ id }),
        GetEventWhatsappBilling({ event_id: id }),
      ]);
      if (res.status === 200) {
        setEventAnalytics(res.data);
      }
      if (billingRes.status === 200 && billingRes.data?.success) {
        setWaCost(billingRes.data.total_cost);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventAnalytics();
  }, []); // eslint-disable-line

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await ExportEventAnalyticsCsv({ id });
      // {event-name}-analytics.csv — spaces (and any other unsafe filename
      // character) become a single dash, with a dash before "analytics" too.
      const safeEventName = (eventAnalytics?.event_name || `${id}`)
        .replace(/\s+/g, "-")
        .replace(/[^A-Za-z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${safeEventName}-analytics.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      message.error("Failed to export analytics.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="card custom-card my-3">
      {loading ? (
        <Skeleton loading={true} active round />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="m-0 text-black">Event Analytics</h5>
            {user?.user_type === "admin" && (
              <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExportCsv}>
                Export
              </Button>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex flex-wrap mt-3  justify-content-start gap-3">
              <div
                className="card custom-card dashboard-card"
                style={{ position: "relative" }}
              >
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={activeUserIcon} alt="Total Active Users" />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.active_user_of_event}</h3>
                    <p>Total Active Users</p>
                  </div>
                  <Tooltip
                    title="Users who have scanned their face at least once."
                    placement="top"
                  >
                    <InfoCircleOutlined
                      className="info-icon"
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={photoDistributedIcon} alt="Photos Distributed Till Now" />
                    <span className="badge bg-dark mt-1 d-block">WhatsApp</span>
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.photo_distributted_count}</h3>
                    <p>Photos Distributed Till Now</p>
                  </div>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={photoDistributedIcon} alt="Photos Distributed Till Now" />
                    <span className="badge bg-dark mt-1 d-block">Web</span>
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.web_photo_distributted_count ?? 0}</h3>
                    <p>Photos Distributed Till Now</p>
                  </div>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <DownloadOutlined style={{ fontSize: 50, color: "#888" }} />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.web_photo_download_count ?? 0}</h3>
                    <p>Total Photos Download</p>
                  </div>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={faceIcon} alt="Unique Faces Found in Photos" />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.unique_faces}</h3>
                    <p>Unique Faces Found in Photos</p>
                  </div>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={totalPhotoIcon} alt="Total Number of Photos" />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.total_photos}</h3>
                    <p>Total Number of Photos</p>
                  </div>
                </div>
              </div>

              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={SystemUpload} alt="System Uploaded Photos" />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.system_uploaded}</h3>
                    <p>System Uploaded Photos</p>
                  </div>
                </div>
              </div>
              <div className="card custom-card dashboard-card">
                <div className="d-flex gap-3">
                  <div className="stat-left">
                    <img src={ImageUpload} alt="User Uploaded Photos" />
                  </div>
                  <div className="stat-right">
                    <h3>{eventAnalytics?.user_contributed}</h3>
                    <p>User Uploaded Photos</p>
                  </div>
                </div>
              </div>

              {user?.user_type === "admin" && (
                <>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={Rekognition}
                          alt="Total Rekognition API Calls"
                          width={60}
                          height={50}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.total_aws_api_calls}</h3>
                        <p>Total Rekognition API Calls</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={DollarSign}
                          alt="Total Rekognition Cost"
                          style={{ width: "40px", height: "40px" }}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.total_aws_api_calls_cost}</h3>
                        <p>Total Rekognition Cost</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={DollarSign}
                          alt="AWS S3 Storage Cost"
                          style={{ width: "40px", height: "40px" }}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.s3_space_cost}</h3>
                        <p>AWS S3 Storage Cost</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={S3Logo}
                          alt="AWS S3 Storage"
                          style={{ width: "40px", height: "40px" }}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.s3_space_in_gb}</h3>
                        <p>AWS S3 Storage</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={DollarSign}
                          alt="CloudFront Bandwidth Cost"
                          style={{ width: "40px", height: "40px" }}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.cloudfront_bandwidth_cost}</h3>
                        <p>CloudFront Bandwidth Cost</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left">
                        <img
                          src={DollarSign}
                          alt="Total Lambda Cost"
                          style={{ width: "40px", height: "40px" }}
                        />
                      </div>
                      <div className="stat-right">
                        <h3>{eventAnalytics?.total_lambda_cost}</h3>
                        <p>Total Lambda Cost</p>
                      </div>
                    </div>
                  </div>
                  <div className="card custom-card dashboard-card">
                    <div className="d-flex gap-3">
                      <div className="stat-left d-flex align-items-center justify-content-center">
                        <span style={{ fontSize: 48, fontWeight: 400, color: "#888", lineHeight: 1 }}>$</span>
                      </div>
                      <div className="stat-right">
                        <h3>
                          {waCost !== null
                            ? `$${Number(waCost).toFixed(4)}`
                            : "$0.0000"}
                        </h3>
                        <p>WhatsApp Cost</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
